/**
 * Le corrigé de l'éditeur en vis-à-vis de la copie, et trois boutons.
 *
 * C'est la moitié de la V2 que Hedi a nommée le 24 septembre 2026 : *« Le parent
 * s'occupe de valider que le travail est correct en regardant la correction »*.
 * L'écran ne conclut rien de lui-même — il pose côte à côte ce qu'il faut pour
 * conclure : l'énoncé tel qu'il est dans le cahier, ce que l'enfant a écrit, et
 * la correction imprimée à la fin du livre.
 *
 * **Le corrigé ne traverse que par ici.** Côté enfant, la clé n'est signée
 * qu'après une tentative enregistrée (règle 2), et cette règle-là ne bouge pas ;
 * le chemin de signature du parent est distinct et vit dans la route
 * (`core-api` §3.25, `web-pwa` §17.4). Un test l'affirme sur le texte HTTP brut
 * des deux côtés.
 *
 * **Le verdict ne se reprend pas**, et c'est la seule décision de cet écran qui
 * lui coûte des lignes. Il écrit une observation de poids 0,80 dans `mastery`,
 * qui est un journal en ajout seul : le reprendre demanderait soit d'écrire une
 * seconde observation sur une même copie, soit d'en effacer une — la première
 * fausse le compte, la seconde est interdite. Le serveur refuse donc (422), et
 * l'écran demande **une confirmation dans la page** plutôt que de poser le
 * verdict au premier appui. Dans la page et non par `window.confirm` : le
 * `WebChromeClient` de la coquille Android ne surcharge que `onShowFileChooser`,
 * si bien qu'un `confirm` y est un refus silencieux (8 septembre 2026).
 *
 * **Le chat vient avant le verdict**, et c'est tout ce qu'il fait : il lève le
 * doute, il ne tranche pas. Le tuteur voit l'énoncé, le corrigé de l'éditeur et
 * la photo de la copie — les trois pages que le parent a lui-même sous les yeux
 * — et jamais le journal du soir de l'enfant (règle 4). Il rend de la prose, pas
 * un verdict : la règle 3 est précisément qu'une lecture de modèle ne vaut pas
 * vérification, et les trois boutons restent les seuls à écrire dans `mastery`.
 *
 * **La copie arrive pré-corrigée** quand un modèle a pu la lire (`core-api`
 * §3.28, `web-pwa` §17.9) : le verdict proposé est **pré-armé**, avec sa phrase,
 * et « Tout confirmer » pose d'un geste tous ceux d'une feuille. Pré-armé et
 * non posé — la confirmation reste celle d'avant, et c'est le parent qui la
 * donne. Une ligne « illisible » ou sans proposition arrive vide, comme avant.
 */

import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { buildUrl, call } from "@/api/client";
import { ApiError } from "@/api/errors";
import type {
  EntrainementOut,
  LigneDEntrainementOut,
  MessageDeValidationOut,
  SujetDonneOut,
} from "@/api/schema";
import { AsseoirACote, peutSAsseoir } from "@/ui/asseoir";
import { EnonceImage, EnonceImages, imageDeLEnonce, imagesDeLEnonce } from "@/ui/EnonceImage";
import { EtiquetteParascolaire } from "@/ui/Parascolaire";
import { enClair } from "@/ui/dates";
import { useEnfants } from "@/ui/enfants";
import { LiensDImpression } from "@/ui/impression";
import { etiquette } from "@/ui/matieres";
import { Button, Card, Empty, ErrorNote, Loading, Screen } from "@/ui/primitives";
import { avantDeJugerLaFeuilleDeLEleve, parChapitre, titreAValider } from "@/ui/feuille";
import { type Choix, ChoixDuVerdict, confirmation, DIT, type Valeur } from "@/v2/ChoixDuVerdict";
import {
  corpsDeLaNote,
  points,
  preArmes,
  restantes,
  type VerdictDeQuestion,
} from "@/v2/noteAuBareme";
import { Depliable } from "@/v2/Depliable";
import { TexteDuParent } from "@/v2/FormePapier";
import { aConfirmer, confirmationDeTout, PROPOSE, verdictPropose } from "@/v2/proposition";

type Envoi = "repos" | "envoi";

/**
 * Le modèle qui a lu, dans les mots du parent.
 *
 * Purement décoratif, et c'est pourquoi il peut vivre ici alors que le barème
 * des verdicts ne le peut pas : un identifiant inconnu est **affiché tel quel**
 * plutôt que traduit de travers, donc cette table ne peut pas mentir en
 * divergeant de celle du serveur — au pire elle ne sert à rien.
 */
const LECTURES: Record<string, string> = {
  "claude-sonnet-5": "lecture rapide",
  "claude-opus-5-5": "lecture approfondie",
  "claude-opus-5": "lecture approfondie",
};

type Demande = "repos" | "rapide" | "approfondie";

/**
 * Le fil tenu devant une copie, et le bouton qui redemande de plus près.
 *
 * **Replié, et chargé seulement à l'ouverture.** Une série porte cinq à dix
 * exercices ; lire les fils avec la série ferait dix lectures journalisées à
 * chaque ouverture de l'écran (§9) pour un fil qui, la plupart du temps,
 * n'existe pas. Le prix est qu'un fil ouvert par l'autre parent ne s'annonce
 * pas : il se découvre en dépliant, et le fil est commun aux deux.
 *
 * **La question reste dans le champ tant qu'elle n'a pas abouti.** Le serveur
 * répond 422 sans rien écrire quand le modèle ne répond pas (règle 1 comprise),
 * et ce qu'un parent vient de taper ne doit pas disparaître avec la panne.
 */
function ChatDeValidation({
  childId,
  ligneId,
  prenom,
}: {
  childId: string;
  ligneId: string;
  prenom: string | undefined;
}) {
  const [ouvert, setOuvert] = useState(false);
  const [fil, setFil] = useState<MessageDeValidationOut[] | null>(null);
  const [question, setQuestion] = useState("");
  const [demande, setDemande] = useState<Demande>("repos");
  const [panne, setPanne] = useState<string | null>(null);

  const ouvrir = async () => {
    setOuvert(true);
    if (fil) return;
    setPanne(null);
    try {
      const charge = await call("chatDeValidation", {
        params: { child_id: childId, ligne_id: ligneId },
      });
      setFil(charge.messages ?? []);
    } catch (echec) {
      setFil([]);
      setPanne(echec instanceof ApiError ? echec.message : "Le fil n'a pas pu être relu.");
    }
  };

  const demander = async (saisie: string, approfondir: boolean) => {
    const posee = saisie.trim();
    if (!posee || demande !== "repos") return;
    setDemande(approfondir ? "approfondie" : "rapide");
    setPanne(null);
    try {
      const apres = await call("poserUneQuestionDeValidation", {
        params: { child_id: childId, ligne_id: ligneId },
        body: { question: posee, approfondir },
      });
      // Le fil entier, tel qu'il est stocké, et non le message ajouté de notre
      // côté : les deux parents écrivent dedans, et l'écran doit montrer ce qui
      // est écrit plutôt que ce qu'il croit avoir envoyé.
      setFil(apres.messages ?? []);
      setQuestion("");
    } catch (echec) {
      setPanne(
        echec instanceof ApiError
          ? echec.message
          : "Le tuteur n'a pas répondu. Votre question est restée dans le champ.",
      );
    } finally {
      setDemande("repos");
    }
  };

  if (!ouvert) {
    return (
      <div className="list__actions">
        <Button variant="quiet" onClick={() => void ouvrir()}>
          Demander au tuteur
        </Button>
      </div>
    );
  }

  const messages = fil ?? [];
  // La dernière question posée, celle que « de plus près » redemande. Redemander
  // écrit une seconde fois la question dans le fil, et c'est voulu : le fil est
  // la trace de ce qui a été demandé et à qui, et c'est le nom du modèle porté
  // par chaque réponse qui distingue les deux lectures.
  const derniere = [...messages].reverse().find((message) => message.role === "parent");
  const attente = demande !== "repos";

  return (
    <div className="validation__chat">
      <p className="muted small">
        Le tuteur voit l'énoncé, la correction de l'éditeur et la photo de la copie. Il ne voit rien
        d'autre — ni le journal du soir de {prenom ?? "l'enfant"}, ni ses résultats. Il explique ;
        c'est vous qui notez.
      </p>

      <div className="chat chat--parent">
        {messages.length === 0 && !attente ? (
          <p className="muted small">Rien n'a encore été demandé sur cet exercice.</p>
        ) : null}
        {messages.map((message) => (
          <article
            key={message.id}
            className={`bubble bubble--${message.role === "parent" ? "child" : "tutor"}`}
          >
            <p>{message.texte}</p>
            {message.role === "tuteur" && message.model ? (
              <p className="muted small">{LECTURES[message.model] ?? message.model}</p>
            ) : null}
          </article>
        ))}
        {attente ? (
          <p className="muted small">
            {demande === "approfondie"
              ? "Le tuteur reprend la copie de plus près — cela peut prendre une minute ou deux…"
              : "Le tuteur regarde la copie…"}
          </p>
        ) : null}
      </div>

      {panne ? <p className="notice notice--attention">{panne}</p> : null}

      <form
        className="composer"
        onSubmit={(evenement) => {
          evenement.preventDefault();
          void demander(question, false);
        }}
      >
        <label className="sr-only" htmlFor={`question-${ligneId}`}>
          Votre question au tuteur
        </label>
        <textarea
          id={`question-${ligneId}`}
          className="composer__input"
          rows={2}
          value={question}
          onChange={(evenement) => setQuestion(evenement.target.value)}
          placeholder="Le résultat est bon mais la rédaction ne ressemble pas au corrigé : est-ce que ça compte ?"
        />
        <Button type="submit" disabled={!question.trim() || attente}>
          Demander
        </Button>
      </form>

      {derniere ? (
        <div className="list__actions">
          <Button
            variant="quiet"
            disabled={attente}
            onClick={() => void demander(derniere.texte, true)}
          >
            Regarde de plus près
          </Button>
          <span className="muted small">
            Reprend la même question avec un modèle plus lent et plus attentif.
          </span>
        </div>
      ) : null}
    </div>
  );
}

function Ligne({
  childId,
  ligne,
  ouvrage,
  prenom,
  rafraichir,
}: {
  childId: string;
  ligne: LigneDEntrainementOut;
  /** Le titre du cahier accompagne l'image partout où elle est rendue, et un
   *  balayage de la source l'affirme (`ui/parascolaire.test.ts`). */
  ouvrage: string;
  prenom: string | undefined;
  rafraichir: () => Promise<void>;
}) {
  const propose = ligne.proposition;
  const [arme, setArme] = useState<Choix | null>(() => verdictPropose(ligne));
  const [envoi, setEnvoi] = useState<Envoi>("repos");
  const [panne, setPanne] = useState<string | null>(null);

  // Toutes les pages : un corrigé à cheval sur deux colonnes du cahier est
  // découpé en deux rectangles, et juger sur le premier seul, c'est juger sur
  // la moitié de la réponse.
  const enonces = imagesDeLEnonce(ligne.enonce_urls, ligne.enonce_url);
  const corriges = imagesDeLEnonce(ligne.corrige_urls, ligne.corrige_url);
  const titre = ligne.numero ? `Exercice ${ligne.numero}` : `Exercice ${ligne.rang}`;

  const juger = async (verdict: Choix) => {
    setEnvoi("envoi");
    setPanne(null);
    try {
      await call("jugerUnEntrainement", {
        params: { child_id: childId, ligne_id: ligne.ligne_id },
        body: { verdict },
      });
      // On relit la série plutôt que de poser le verdict de notre côté : c'est
      // le serveur qui décide de ce qui reste à juger, et lui seul sait si la
      // série entière vient de sortir de la file.
      await rafraichir();
    } catch (echec) {
      setPanne(echec instanceof ApiError ? echec.message : "Le verdict n'est pas parti.");
      setEnvoi("repos");
      setArme(null);
    }
  };

  // Déjà jugé : la ligne reste affichée — la série entière est rendue, pour que
  // le parent voie où il en est —, sans le moindre bouton.
  if (ligne.verdict) {
    return (
      <li className="list__item">
        <div>
          <strong>{titre}</strong>
          {ligne.page ? <span className="muted small"> · page {ligne.page}</span> : null}
          <p className="muted small">
            {ligne.verdict === "pas_fait"
              ? "Pas fait — revenu dans les exercices à tirer"
              : `Jugé ${DIT[ligne.verdict] ?? ligne.verdict}`}
            {ligne.verdict_at ? ` le ${enClair(ligne.verdict_at)}` : ""}
          </p>
        </div>
      </li>
    );
  }

  return (
    <li className="list__item list__item--pile">
      <p className="devoirs__entete">
        <strong>{titre}</strong>
        {ligne.page ? <span className="muted small"> · page {ligne.page}</span> : null}
      </p>
      <p className="muted small">{ligne.competence}</p>
      {/* Le parent qui préfère refaire l'exercice avec l'enfant plutôt que de
          juger la copie seul (lot P4). Le verdict posé là-bas vide cette ligne. */}
      {peutSAsseoir(ligne, true) ? (
        <p>
          <AsseoirACote childId={childId} cible={{ ligneId: ligne.ligne_id }} />
        </p>
      ) : null}

      <EtiquetteParascolaire nom={ouvrage} />

      {/* L'énoncé d'abord : sans lui, le corrigé est une suite de résultats. Le
          texte n'existe pas de notre côté — un ouvrage est découpé en images —,
          donc `EnonceImage` n'a rien d'autre à annoncer. */}
      <EnonceImages images={enonces} statement="" />

      {/* Une forme sur cahier (lot P3) : la grille propre à la forme — accords,
          homophones, ponctuation pour une dictée ; retenues, alignement,
          résultat pour une opération posée —, et le texte ou le résultat quand
          l'exercice n'a pas d'image d'éditeur. */}
      <TexteDuParent ligne={ligne} />

      {/* **Sans photo, le verdict reste possible** — arbitrage de Hedi le
          25 septembre 2026. Le parent est souvent à côté de l'enfant, cahier
          ouvert : exiger une photo ajouterait un geste à l'enfant pour un
          adulte qui a déjà la copie sous les yeux, et la file resterait pleine
          d'exercices en réalité relus. Ce qui manque est **dit**, parce qu'un
          écran sans photo et un écran dont la photo n'a pas chargé se
          ressemblent. */}
      {ligne.photo_url ? (
        <>
          <p className="muted small">Ce que {prenom ?? "l'enfant"} a écrit :</p>
          <a href={ligne.photo_url} target="_blank" rel="noreferrer">
            {/* La photo occupe la largeur et non une vignette : c'est le contenu
                de l'écran, et une écriture d'enfant ne se relit pas dans 160
                pixels. Le lien ouvre l'original, pour zoomer au-delà. */}
            <img
              className="validation__photo"
              src={ligne.photo_url}
              alt="La copie photographiée"
              loading="lazy"
            />
          </a>
        </>
      ) : (
        <p className="muted small">
          Pas de photo : {prenom ?? "l'enfant"} n'en a pas envoyé. Vous pouvez juger sur le cahier.
        </p>
      )}

      {/* Le corrigé est **déplié**, à la différence de la file de validation de
          `/parents` où il est replié pour ne pas faire lire la réponse avant la
          copie. Les deux écrans ne demandent pas la même chose : là, une grille
          générée avec l'exercice permet de conclure sans la réponse ; ici, le
          corrigé de l'éditeur *est* l'instrument — le replier ajouterait un
          geste à la seule chose que le parent est venu faire. Il vient après la
          copie, l'ordre restant celui du travail : regarder, puis vérifier. */}
      {corriges.length ? (
        <>
          <p className="muted small">La correction de l'éditeur :</p>
          <EnonceImages images={corriges} statement="" />
        </>
      ) : (
        <p className="muted small">
          Ce cahier n'a pas de corrigé découpé pour cet exercice. Le verdict reste possible : c'est
          vous qui relisez.
        </p>
      )}

      {/* Le chat **entre le corrigé et les boutons**, parce que c'est l'ordre du
          doute : on regarde, on vérifie, on demande si l'on hésite encore, et
          on note. Le poser après les verdicts le proposerait à quelqu'un qui a
          déjà tranché, sur une décision qui ne se reprend pas. */}
      <ChatDeValidation childId={childId} ligneId={ligne.ligne_id} prenom={prenom} />

      {panne ? <ErrorNote>{panne}</ErrorNote> : null}

      {/* La proposition **juste au-dessus des boutons** : elle est ce qui les a
          pré-armés, et un bouton coloré sans sa raison se lirait comme un
          verdict déjà posé. Le parent la lit, puis confirme ou change. */}
      {propose ? (
        <p className="notice">
          Proposition du tuteur : <strong>{PROPOSE[propose.verdict] ?? propose.verdict}</strong>
          {propose.justification ? ` — ${propose.justification}` : ""}
        </p>
      ) : null}

      <ChoixDuVerdict choisi={arme} choisir={setArme} desactive={envoi === "envoi"} pasFait />
      {arme ? (
        <div className="list__actions">
          <span className="muted small">{confirmation(arme)}</span>
          <Button disabled={envoi === "envoi"} onClick={() => void juger(arme)}>
            Confirmer
          </Button>
          <Button variant="quiet" disabled={envoi === "envoi"} onClick={() => setArme(null)}>
            Annuler
          </Button>
        </div>
      ) : null}
    </li>
  );
}

function Entrainement({
  childId,
  entrainement,
  prenom,
  rafraichir,
}: {
  childId: string;
  entrainement: EntrainementOut;
  prenom: string | undefined;
  rafraichir: () => Promise<void>;
}) {
  const lignes = entrainement.lignes ?? [];
  const jugees = entrainement.jugees ?? 0;
  const confirmables = aConfirmer(lignes);
  const restantes = lignes.filter((ligne) => !ligne.verdict).length - confirmables.length;
  const [toutArme, setToutArme] = useState(false);
  const [envoi, setEnvoi] = useState<Envoi>("repos");
  const [panne, setPanne] = useState<string | null>(null);

  /** Une route par ligne, **l'une après l'autre** : la même que le bouton
   *  « Confirmer » d'une ligne, donc la même observation et le même refus d'un
   *  second verdict. Une panne au milieu s'arrête là et relit la feuille — ce
   *  qui est parti est jugé, le reste attend, et l'écran le montre. */
  const toutConfirmer = async () => {
    setEnvoi("envoi");
    setPanne(null);
    try {
      for (const { ligneId, verdict } of confirmables) {
        await call("jugerUnEntrainement", {
          params: { child_id: childId, ligne_id: ligneId },
          body: { verdict },
        });
      }
    } catch (echec) {
      setPanne(
        echec instanceof ApiError
          ? echec.message
          : "Une partie des verdicts n'est pas partie. Ce qui reste à juger est affiché.",
      );
    } finally {
      setToutArme(false);
      setEnvoi("repos");
      await rafraichir();
    }
  };

  const pdf = (quoi: "imprimerLaFeuille" | "imprimerLeCorrige") =>
    buildUrl(quoi, { params: { child_id: childId, entrainement_id: entrainement.id } });

  return (
    <Depliable
      titre={titreAValider(entrainement, prenom)}
      etat={`pas encore corrigée — ${jugees} jugé${jugees > 1 ? "s" : ""} sur ${lignes.length}`}
      liens={
        <>
          <a
            className="btn btn--quiet"
            href={pdf("imprimerLaFeuille")}
            target="_blank"
            rel="noreferrer"
          >
            La feuille en PDF
          </a>
          <a
            className="btn btn--quiet"
            href={pdf("imprimerLeCorrige")}
            target="_blank"
            rel="noreferrer"
          >
            Le corrigé en PDF
          </a>
        </>
      }
    >
      {/* Une feuille composée par l'élève (5 octobre 2026) : il voit les
          corrigés en la clôturant, donc seule une photo prise avant se juge. */}
      {avantDeJugerLaFeuilleDeLEleve(entrainement, prenom) ? (
        <p className="muted small">{avantDeJugerLaFeuilleDeLEleve(entrainement, prenom)}</p>
      ) : null}
      {confirmables.length ? (
        <div className="list__actions">
          {toutArme ? (
            <>
              <span className="muted small">
                {confirmationDeTout(confirmables.length, restantes)}
              </span>
              <Button disabled={envoi === "envoi"} onClick={() => void toutConfirmer()}>
                Confirmer
              </Button>
              <Button
                variant="quiet"
                disabled={envoi === "envoi"}
                onClick={() => setToutArme(false)}
              >
                Annuler
              </Button>
            </>
          ) : (
            <Button onClick={() => setToutArme(true)}>
              Tout confirmer ({confirmables.length})
            </Button>
          )}
        </div>
      ) : null}
      {panne ? <ErrorNote>{panne}</ErrorNote> : null}

      {parChapitre(lignes).map((bloc) => (
        <section key={`${bloc.ouvrageTitre}/${bloc.chapitre}`}>
          <p className="card__subtitle">{bloc.titre}</p>
          <p className="muted small">
            {bloc.ouvrageTitre} — {etiquette(bloc.matiere, bloc.matiereLibelle)}
          </p>
          <ul className="list">
            {bloc.lignes.map((ligne) => (
              <Ligne
                key={ligne.ligne_id}
                childId={childId}
                ligne={ligne}
                ouvrage={bloc.ouvrageTitre}
                prenom={prenom}
                rafraichir={rafraichir}
              />
            ))}
          </ul>
        </section>
      ))}
    </Depliable>
  );
}

/**
 * Un sujet du dimanche à barème publié : un verdict **par question**, pré-armé
 * depuis la lecture de la copie par le modèle, confirmé par le parent.
 *
 * C'est la confirmation qui écrit la note sur 20 et les observations de
 * maîtrise, une par compétence du barème (`core-api` §3.32) — jamais la
 * lecture. La note n'est pas calculée ici : le serveur la rend, et l'écran
 * l'affiche après coup. « Pas fait » vaut zéro point et n'écrit pas
 * d'observation.
 */
function NoteAuBareme({
  childId,
  sujet,
  commentaire,
  rafraichir,
}: {
  childId: string;
  sujet: SujetDonneOut;
  commentaire: string;
  rafraichir: () => Promise<void>;
}) {
  const questions = sujet.questions ?? [];
  const [verdicts, setVerdicts] = useState(() => preArmes(questions));
  const [arme, setArme] = useState(false);
  const [envoi, setEnvoi] = useState<Envoi>("repos");
  const [panne, setPanne] = useState<string | null>(null);
  const corps = corpsDeLaNote(questions, verdicts);
  const reste = restantes(questions, verdicts);

  const noter = async () => {
    if (!corps) return;
    setEnvoi("envoi");
    setPanne(null);
    try {
      await call("noterUnSujet", {
        params: { child_id: childId, sujet_donne_id: sujet.id },
        body: { questions: corps, commentaire: commentaire.trim() || null },
      });
      await rafraichir();
    } catch (echec) {
      setPanne(echec instanceof ApiError ? echec.message : "La note n'est pas partie.");
      setEnvoi("repos");
      setArme(false);
    }
  };

  return (
    <>
      <p className="muted small">
        Barème : {sujet.bareme_source ?? "publié avec le sujet"}. Le tuteur a lu la copie et
        propose un verdict par question ; c'est le vôtre qui compte.
      </p>
      <ul className="list">
        {questions.map((question) => (
          <li key={question.numero} className="list__item">
            <p>
              Question {question.numero} — {question.intitule}{" "}
              <span className="muted small">({points(question.points_max)})</span>
            </p>
            {question.proposition ? (
              <p className="muted small">
                Le tuteur propose : {DIT[question.proposition.verdict] ?? "illisible"} —{" "}
                {question.proposition.justification}
              </p>
            ) : null}
            <ChoixDuVerdict<VerdictDeQuestion>
              choisi={verdicts[question.numero] ?? null}
              choisir={(valeur) => {
                setArme(false);
                setVerdicts((avant) => ({ ...avant, [question.numero]: valeur }));
              }}
              desactive={envoi === "envoi"}
              pasFait
            />
          </li>
        ))}
      </ul>
      {panne ? <ErrorNote>{panne}</ErrorNote> : null}
      {reste > 0 ? (
        <p className="muted small">
          {reste} question{reste > 1 ? "s attendent" : " attend"} encore votre verdict.
        </p>
      ) : arme ? (
        <div className="list__actions">
          <span className="muted small">
            Noter ce sujet au barème ? La note sur 20 sera visible par l'élève. Cela ne se
            reprend pas.
          </span>
          <Button disabled={envoi === "envoi"} onClick={() => void noter()}>
            Confirmer
          </Button>
          <Button variant="quiet" disabled={envoi === "envoi"} onClick={() => setArme(false)}>
            Annuler
          </Button>
        </div>
      ) : (
        <Button onClick={() => setArme(true)}>Noter sur 20</Button>
      )}
    </>
  );
}

/**
 * Un sujet entier rendu, jugé **en entier** avec le corrigé de l'éditeur.
 *
 * Même ordre que pour un exercice — la copie, puis la correction, puis le
 * verdict —, mais le verdict **n'écrit rien dans `mastery`** : un sujet couvre
 * plusieurs compétences et n'est pas vérifié (règle 3). Il ne se reprend pas
 * non plus, d'où la même confirmation dans la page. Le commentaire est
 * facultatif et reste entre parents : l'enfant ne voit ni le verdict ni le
 * commentaire (§14.1).
 */
function SujetAJuger({
  childId,
  sujet,
  prenom,
  rafraichir,
}: {
  childId: string;
  sujet: SujetDonneOut;
  prenom: string | undefined;
  rafraichir: () => Promise<void>;
}) {
  const [arme, setArme] = useState<Valeur | null>(null);
  const [commentaire, setCommentaire] = useState("");
  const [envoi, setEnvoi] = useState<Envoi>("repos");
  const [panne, setPanne] = useState<string | null>(null);
  const photos = sujet.photos ?? [];
  const ouvrage = sujet.ouvrage_titre ?? "Recueil de sujets";
  const enonces = (sujet.enonce_urls ?? []).map(imageDeLEnonce);
  const corriges = (sujet.corrige_urls ?? []).map(imageDeLEnonce);

  const juger = async (verdict: Valeur) => {
    setEnvoi("envoi");
    setPanne(null);
    try {
      await call("jugerUnSujet", {
        params: { child_id: childId, sujet_donne_id: sujet.id },
        body: { verdict, commentaire: commentaire.trim() || null },
      });
      await rafraichir();
    } catch (echec) {
      setPanne(echec instanceof ApiError ? echec.message : "Le verdict n'est pas parti.");
      setEnvoi("repos");
      setArme(null);
    }
  };

  return (
    <Depliable
      titre={`Sujet entier : ${sujet.titre}`}
      etat={sujet.rendu_at ? `rendu le ${enClair(sujet.rendu_at)} — à corriger` : "à corriger"}
      liens={
        <LiensDImpression
          cible={{ quoi: "sujet", childId, sujetDonneId: sujet.id }}
          corrige={corriges.length > 0}
        />
      }
    >
      <p className="muted small">
        {sujet.chapitre_titre}
        {sujet.page != null ? ` · page ${sujet.page}` : ""}
        {sujet.rendu_at ? ` · rendu le ${enClair(sujet.rendu_at)}` : ""}
      </p>

      <EtiquetteParascolaire nom={ouvrage} />
      <details>
        <summary>L'énoncé ({enonces.length} page{enonces.length > 1 ? "s" : ""})</summary>
        {enonces.map((image, rang) =>
          image ? <EnonceImage key={rang} image={image} statement="" /> : null,
        )}
      </details>

      <p className="muted small">
        La copie de {prenom ?? "l'enfant"}, {photos.length} page{photos.length > 1 ? "s" : ""} :
      </p>
      {photos.map((photo, rang) =>
        photo.url ? (
          <a key={photo.id} href={photo.url} target="_blank" rel="noreferrer">
            <img
              className="validation__photo"
              src={photo.url}
              alt={`Page ${rang + 1} de la copie`}
              loading="lazy"
            />
          </a>
        ) : null,
      )}

      {corriges.length > 0 ? (
        <>
          <p className="muted small">La correction de l'éditeur :</p>
          {corriges.map((image, rang) =>
            image ? <EnonceImage key={rang} image={image} statement="" /> : null,
          )}
        </>
      ) : (
        <p className="muted small">
          Ce sujet n'a pas de corrigé découpé. Le verdict reste possible : c'est vous qui relisez.
        </p>
      )}

      <label className="muted small" htmlFor={`commentaire-${sujet.id}`}>
        Un commentaire, si vous voulez (entre parents, l'enfant ne le voit pas)
      </label>
      <textarea
        id={`commentaire-${sujet.id}`}
        className="composer__input"
        rows={2}
        maxLength={2000}
        value={commentaire}
        onChange={(evenement) => setCommentaire(evenement.target.value)}
      />

      {panne ? <ErrorNote>{panne}</ErrorNote> : null}

      {(sujet.questions ?? []).length > 0 ? (
        <NoteAuBareme
          childId={childId}
          sujet={sujet}
          commentaire={commentaire}
          rafraichir={rafraichir}
        />
      ) : (
        <>
          {sujet.proposition ? (
            <p className="muted small">
              Le tuteur a lu la copie et propose : {DIT[sujet.proposition.verdict] ?? "illisible"}{" "}
              — {sujet.proposition.justification}
            </p>
          ) : null}
          <ChoixDuVerdict choisi={arme} choisir={setArme} desactive={envoi === "envoi"} />
        </>
      )}
      {arme ? (
        <div className="list__actions">
          <span className="muted small">
            Noter ce sujet {DIT[arme]} ? Cela ne se reprend pas.
          </span>
          <Button disabled={envoi === "envoi"} onClick={() => void juger(arme)}>
            Confirmer
          </Button>
          <Button variant="quiet" disabled={envoi === "envoi"} onClick={() => setArme(null)}>
            Annuler
          </Button>
        </div>
      ) : null}
    </Depliable>
  );
}

export function ValiderScreen() {
  const { childId = "" } = useParams();
  const enfants = useEnfants();
  const prenom = enfants.find((enfant) => enfant.child_id === childId)?.display_name;
  const [entrainements, setEntrainements] = useState<EntrainementOut[] | null>(null);
  const [sujets, setSujets] = useState<SujetDonneOut[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  const charger = useCallback(async () => {
    const params = { child_id: childId };
    const [charge, rendus] = await Promise.all([
      call("entrainementsAJuger", { params }),
      call("sujetsAJuger", { params }),
    ]);
    setEntrainements(charge.entrainements ?? []);
    setSujets(rendus.sujets ?? []);
  }, [childId]);

  useEffect(() => {
    setEntrainements(null);
    setSujets(null);
    setErreur(null);
    void (async () => {
      try {
        await charger();
      } catch (echec) {
        setErreur(echec instanceof ApiError ? echec.message : "Chargement impossible.");
      }
    })();
  }, [charger]);

  return (
    <Screen
      title="À valider"
      lead={prenom ? `Ce que ${prenom} a rendu et qui attend une relecture` : undefined}
    >
      {erreur ? <ErrorNote>{erreur}</ErrorNote> : null}
      {!entrainements && !erreur ? <Loading /> : null}

      {entrainements && entrainements.length === 0 && sujets && sujets.length === 0 ? (
        <Card>
          {/* Deux causes, une seule phrase : rien n'a été préparé, ou tout ce
              qui a été rendu est déjà jugé. Elles appellent le même geste — il
              n'y a rien à faire ici —, et l'écran voisin les distingue. */}
          <Empty>
            Rien n'attend de relecture. Ce qui a été rendu est jugé, et le reste n'est pas encore
            fait.
          </Empty>
        </Card>
      ) : null}

      {(sujets ?? []).map((sujet) => (
        <SujetAJuger
          key={sujet.id}
          childId={childId}
          sujet={sujet}
          prenom={prenom}
          rafraichir={charger}
        />
      ))}

      {(entrainements ?? []).map((entrainement) => (
        <Entrainement
          key={entrainement.id}
          childId={childId}
          entrainement={entrainement}
          prenom={prenom}
          rafraichir={charger}
        />
      ))}
    </Screen>
  );
}
